import { DollarSign, Coffee, Package, Gift, Briefcase } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { OpportunityData } from "./schemas/opportunityFormSchemas";

interface PricingDetailsPanelProps {
  opportunity: OpportunityData;
}

export const PricingDetailsPanel = ({ opportunity }: PricingDetailsPanelProps) => {
  const getFeeTypeLabel = (feeType: string): string => {
    switch (feeType) {
      case "total":
        return "(total)";
      case "per_event":
        return "(per event)";
      case "per_volunteer":
        return "(per volunteer)";
      default:
        return "";
    }
  };

  const renderCurrencyIcon = (currency: string) => {
    switch (currency) {
      case "USD":
        return <DollarSign className="h-4 w-4 text-gray-500" />;
      case "EUR":
        return <span className="h-4 w-4 text-gray-500">€</span>;
      case "GBP":
        return <span className="h-4 w-4 text-gray-500">£</span>;
      case "JPY":
        return <span className="h-4 w-4 text-gray-500">¥</span>;
      case "CAD":
        return <span className="h-4 w-4 text-gray-500">C$</span>;
      case "AUD":
        return <span className="h-4 w-4 text-gray-500">A$</span>;
      case "SGD":
        return <span className="h-4 w-4 text-gray-500">S$</span>;
      case "INR":
        return <span className="h-4 w-4 text-gray-500">₹</span>;
      default:
        return <DollarSign className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h4 className="font-medium flex items-center gap-2 mb-4">
        <DollarSign className="h-4 w-4 text-green-500" />
        Rate Card Information
      </h4>

      <div className="space-y-3">
        <div className="flex justify-between items-center pb-2 border-b">
          <span className="text-sm font-medium">Base Price:</span>
          <div className="flex items-center">
            <span className="font-bold text-lg flex items-center gap-1">
              {renderCurrencyIcon(opportunity.pricing_details.currency)}
              <span>{opportunity.pricing_details.cost_per_unit}</span>
            </span>
            <span className="text-xs text-gray-500 ml-1">
              {opportunity.pricing_details.pricing_model === "per_volunteer"
                ? "/ volunteer"
                : "/ event"}
            </span>
          </div>
        </div>

        <div className="text-sm">
          <div className="flex justify-between py-1">
            <span>Min Volunteers:</span>
            <span>{opportunity.catalog_data.min_volunteers}</span>
          </div>
          <div className="flex justify-between py-1">
            <span>Max Volunteers:</span>
            <span>{opportunity.catalog_data.max_volunteers}</span>
          </div>
        </div>

        {/* Additional Fees */}
        {(!opportunity.pricing_details.include_fees_in_base ||
          opportunity.pricing_details.additional_fees.program_management.amount > 0 ||
          opportunity.pricing_details.additional_fees.material_kit.amount > 0 ||
          opportunity.pricing_details.additional_fees.refreshments.amount > 0 ||
          opportunity.pricing_details.additional_fees.goodies.amount > 0) && (
          <>
            <Separator className="my-2" />
            <h5 className="font-medium text-sm mb-2">Additional Fees:</h5>
            <div className="space-y-2 text-sm">
              {opportunity.pricing_details.additional_fees.program_management.amount > 0 && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                    <span>
                      Program Management{" "}
                      {getFeeTypeLabel(
                        opportunity.pricing_details.additional_fees.program_management.fee_type
                      )}
                    </span>
                  </div>
                  <span className="flex items-center gap-1">
                    {renderCurrencyIcon(opportunity.pricing_details.currency)}
                    <span>{opportunity.pricing_details.additional_fees.program_management.amount}</span>
                  </span>
                </div>
              )}

              {opportunity.pricing_details.additional_fees.material_kit.amount > 0 && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1">
                    <Package className="h-3.5 w-3.5 text-gray-400" />
                    <span>
                      Material/Kit{" "}
                      {getFeeTypeLabel(
                        opportunity.pricing_details.additional_fees.material_kit.fee_type
                      )}
                    </span>
                  </div>
                  <span className="flex items-center gap-1">
                    {renderCurrencyIcon(opportunity.pricing_details.currency)}
                    <span>{opportunity.pricing_details.additional_fees.material_kit.amount}</span>
                  </span>
                </div>
              )}

              {opportunity.pricing_details.additional_fees.refreshments.amount > 0 && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1">
                    <Coffee className="h-3.5 w-3.5 text-gray-400" />
                    <span>
                      Refreshments{" "}
                      {getFeeTypeLabel(
                        opportunity.pricing_details.additional_fees.refreshments.fee_type
                      )}
                    </span>
                  </div>
                  <span className="flex items-center gap-1">
                    {renderCurrencyIcon(opportunity.pricing_details.currency)}
                    <span>{opportunity.pricing_details.additional_fees.refreshments.amount}</span>
                  </span>
                </div>
              )}

              {opportunity.pricing_details.additional_fees.goodies.amount > 0 && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1">
                    <Gift className="h-3.5 w-3.5 text-gray-400" />
                    <span>
                      Goodies{" "}
                      {getFeeTypeLabel(
                        opportunity.pricing_details.additional_fees.goodies.fee_type
                      )}
                    </span>
                  </div>
                  <span className="flex items-center gap-1">
                    {renderCurrencyIcon(opportunity.pricing_details.currency)}
                    <span>{opportunity.pricing_details.additional_fees.goodies.amount}</span>
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Pricing Model */}
        <Separator className="my-2" />
        <div className="bg-blue-50 p-3 rounded-md text-sm">
          <p className="font-medium text-blue-700 mb-1">Pricing Model:</p>
          <p className="text-blue-600">
            {opportunity.pricing_details.include_fees_in_base
              ? "All fees included in base price"
              : "Fees itemized separately"}
          </p>
        </div>

        {/* Additional Details */}
        {opportunity.pricing_details.additional_details && (
          <div className="mt-3 text-sm text-gray-700">
            <p className="font-medium">Additional Notes:</p>
            <p>{opportunity.pricing_details.additional_details}</p>
          </div>
        )}
      </div>
    </div>
  );
};
